import { useEffect, useState } from "react";

import { useCaseOverview, useCases, useHandoverNotices, useIncomingHandovers } from "@/api";
import { HandoverNotices } from "@/features/handover/HandoverNotices";
import { IncomingHandovers } from "@/features/handover/IncomingHandovers";
import { isMail } from "@/lib/mail";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

import { EntryAreas } from "./EntryAreas";
import { Greeting } from "./Greeting";
import { RELEASE_STATUSES, sayingFor } from "./pickGreeting";
import { RecentCases } from "./RecentCases";
import styles from "./StartScreen.module.css";
import { useMailDrop } from "./useMailDrop";

/**
 * A query has answered once it holds data or an error. React Query puts a
 * query without data back to `pending` while it fetches again after a
 * failure, error and all; that is still an answer, or every „Erneut laden"
 * would blank the page back to its loading state.
 */
function answered(query: { isPending: boolean; errorUpdateCount?: number }): boolean {
  return !query.isPending || (query.errorUpdateCount ?? 0) > 0;
}

/**
 * The start page (design 2026-09-07, UX gate round 1, #1): the work first,
 * then the way in. One order on every width — the greeting in two lines;
 * the outcomes of my requests and the requests to me, each block only when
 * it has something to say; „Zuletzt bearbeitet"; the two steel areas; the
 * promise of the manual way; the Spruch des Tages at the foot. Whoever has Vorgänge meets the steel areas
 * compact (≈ 160 px, the mesh as an edge); at full height only on the first
 * visit, where starting *is* the work and there is no list to show. The list
 * is a preview of five (design 2026-09-22 §8.4); the whole register lives at
 * `/vorgaenge`.
 *
 * The greeting waits for the case list, not the other way round: what it says
 * depends on what waits in the list, so drawing it while the list is still
 * pending would greet a first-time visitor every single time.
 *
 * The page waits for the handovers in the same way (Task 13 review, I2): a
 * block of unknown height that arrives late pushes down whatever stands under
 * it. So the page counts as settled only once the case list and both handover
 * queries have answered (a failure is an answer too). Until then it shows its
 * loading state — the held greeting space and nothing under it — and shows
 * the rest in one go. Nothing is reserved for a block that may not come, and
 * nothing already on screen is moved by what comes.
 *
 * The blocks are mounted from the start and only `hidden`: their queries run
 * in parallel with the page's own, and an observer that mounted only after a
 * failed first load would fetch once more on its own (React Query retries an
 * errored query on mount) and flash „Wird geladen …" on its button.
 *
 * While it waits, the page is a viewport high. A shorter page lets the
 * shell's grid stretch its rows on a phone (the content jumped 124 px up
 * once it arrived); on a desk it used to push the left sidebar along as
 * well, which has gone since. Settled, the start page is usually taller than
 * that on its own.
 */
export function StartScreen() {
  useDocumentTitle("Start");
  const cases = useCases();
  // The cases list carries no calculation; the register's volume does. Asked
  // only for the stages the greeting counts as „waiting for my release", so
  // a blocked calculation is not announced as one (15.10.2026).
  const releasable = useCaseOverview({ status: [...RELEASE_STATUSES], limit: 500 });
  const incoming = useIncomingHandovers();
  const notices = useHandoverNotices();
  const [hovering, setHovering] = useState(false);
  // The mail's way in lives here, not in the drop area: a mail is taken
  // wherever on the page it is let go, and the filing's messages stand under
  // the two areas whichever way it came.
  const mail = useMailDrop();
  const { receive: receiveMail } = mail;
  // Spruch des Tages: read once per visit, like the greeting's own draw.
  const [saying] = useState(() => sayingFor(new Date()));

  // A file dragged anywhere over the page lifts the left area and dims the
  // rest (§6, §8). On `document`, not on the area, because that is the point:
  // the invitation has to be visible before the pointer has found the target.
  //
  // `dragleave` fires on every boundary *inside* the page as well, so a plain
  // handler would flicker the whole way to the drop zone. The short timer lets
  // the next `dragover` — which follows within a frame while the file is still
  // over the page — cancel the retreat.
  useEffect(() => {
    let leaveTimer: number | undefined;

    const over = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes("Files")) return;
      event.preventDefault();
      window.clearTimeout(leaveTimer);
      setHovering(true);
    };
    const leave = () => {
      leaveTimer = window.setTimeout(() => setHovering(false), 120);
    };
    // `preventDefault` is the point, not the tidying up: without it a file let
    // go anywhere outside the drop zone makes the *browser* open it, which
    // throws the whole app away mid-Vorgang. The page says „drop a drawing
    // here", so the page has to answer for every drop on it.
    //
    // **A mail is the exception to „ignored":** let go anywhere on the page it
    // is filed as a new Vorgang, together with the drawings dropped with it.
    // A drawing outside the drop area is still ignored — it has to be aimed.
    // A drop *inside* the drop area was taken by that area's own handler
    // (React's, which runs before this one) and is not taken twice.
    const drop = (event: DragEvent) => {
      event.preventDefault();
      window.clearTimeout(leaveTimer);
      setHovering(false);
      if (event.target instanceof Element && event.target.closest("[data-drop-area]")) return;
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.some(isMail)) void receiveMail(files);
    };

    document.addEventListener("dragover", over);
    document.addEventListener("dragleave", leave);
    document.addEventListener("drop", drop);
    return () => {
      document.removeEventListener("dragover", over);
      document.removeEventListener("dragleave", leave);
      document.removeEventListener("drop", drop);
      window.clearTimeout(leaveTimer);
    };
  }, [receiveMail]);

  const settled = answered(cases) && answered(releasable) && answered(incoming) && answered(notices);
  const all = cases.data ?? [];
  // Not known (still failing) is `null`: the greeting then counts by status alone, as before.
  const blocked = releasable.data
    ? new Set(releasable.data.items.filter((item) => (item.volume?.blockerCodes.length ?? 0) > 0).map((item) => item.id))
    : null;

  // A list that could not be fetched is not an empty list. Greeting a
  // long-standing user as a newcomer because the server was briefly away
  // states something false about their work. Nothing known → `null`, and the
  // greeting says nothing about the load. That holds while it fetches again
  // after the failure, too (see `answered`).
  const failed = cases.data === undefined && (cases.isError || (cases.errorUpdateCount ?? 0) > 0);
  const dim = `${styles.muted} ${hovering ? styles.dimmed : ""}`;
  // A first visit — nothing of mine yet, and known to be nothing — has no
  // list to show and meets the two ways in at full height. Everyone else
  // meets them compact, under the list. A list that failed is not an empty
  // one: the page keeps the returning user's shape and says so in the list.
  const firstVisit = !failed && all.length === 0;
  const waiting = Array.isArray(incoming.data) ? incoming.data : null;

  // One order on every width (screen check S-7, UX gate round 1, #1): the
  // DOM is the visual order, so Tab and a screen reader meet the blocks
  // where the eye does (Task 13 review, I1), and no media query moves them.
  return (
    <div className={`${styles.screen} ${settled ? "" : styles.settling}`} aria-busy={settled ? undefined : true}>
      {settled ? (
        <div className={dim}>
          <Greeting
            cases={failed ? null : all}
            handovers={waiting?.map((item) => ({ fromDisplayName: item.handover.fromDisplayName, title: item.title })) ?? null}
            blocked={blocked}
            isFirstVisit={firstVisit}
          />
        </div>
      ) : (
        <div className={styles.greetingSpace} />
      )}
      {/* Outcomes of my own requests, then requests to me (design §8.3). */}
      <div hidden={!settled} className={`${styles.handovers} ${dim}`}>
        <HandoverNotices />
        <IncomingHandovers />
      </div>
      <div hidden={!settled || firstVisit} className={`${styles.lower} ${dim}`}>
        <RecentCases />
      </div>
      <div hidden={!settled}>
        <EntryAreas compact={!firstVisit} fileHovering={hovering} mail={mail} />
      </div>
      {/* The sentence that stood at the old sidebar's foot (spec 2026-10-22
          §4): a promise (A-014, E-45), so word for word. It belongs where every
          session begins, under the two ways in — and before the saying, which
          is an extra and so comes last. */}
      <p hidden={!settled} className={`${styles.promise} ${dim}`}>
        Nichts wird produktionsverbindlich ohne Bestätigung. Der manuelle Weg bleibt in jedem Schritt offen.
      </p>
      <p hidden={!settled} className={`${styles.saying} ${dim}`} data-testid="saying">
        {saying}
      </p>
    </div>
  );
}
